import { EntityPage } from "@/pages/digital-files/entity";
import { EntityRegistryPage } from "@/pages/digital-files/entity-registry";
import { RegistryDetailPage } from "@/pages/digital-files/entity-registry/detail-page";
import FormCorrection from "@/pages/procedures/components/form/form-correction";
import { FormDetail } from "@/pages/procedures/components/form/form-detail";
import { FormInterns } from "@/pages/procedures/components/form/form-interns";
import { ProcedureFormsList } from "@/pages/procedures/components/form/form-list";
import { PaymentDetail } from "@/pages/procedures/components/payment/PaymentDetail";
import ResolutionPage from "@/pages/procedures/components/resolution/ResolutionDetail";
import SignatureDetail from "@/pages/procedures/components/signature/signature-detail";
import RoleGuard from "@/pages/role-guard";
import { createEntityDetailsBreadcrumb } from "@/queries/digital-files/entities.api";
import { createRegistryDetailsBreadcrumb } from "@/queries/digital-files/registries.api";
import {
  createFormalityRequestBreadcrumb,
  createGetFormDetailBreadcrumb,
} from "@/queries/formality-request";
import { Permissions } from "@/utils/permissions";
import type { QueryClient } from "@tanstack/react-query";
import { lazy } from "react";
import { makeHandle, withNotFoundRedirect } from "./router.helpers";

const DigitalFilesPage = lazy(() => import("@/pages/digital-files"));

const ProcedureDetailPage = lazy(() =>
  import("@/pages/procedures").then((m) => ({
    default: m.ProcedureDetailPage,
  })),
);

type RegistryParams = {
  id?: string;
  registryId?: string;
  procedureId?: string;
};

const registryBase = (params: RegistryParams) =>
  `/expedientes/entidad/${params?.id}/detalle-registro/${params?.registryId}`;

const registryProcedureBase = (params: RegistryParams) =>
  `${registryBase(params)}/tramite/${params?.procedureId}`;

export const createExpedientesRoutes = (queryClient: QueryClient) => [
  {
    path: "/expedientes",
    element: (
      <RoleGuard permissions={[Permissions.expedient_base_entities.read]}>
        <DigitalFilesPage />
      </RoleGuard>
    ),
    handle: makeHandle({ label: "Expedientes" }),
  },
  {
    path: "/expedientes/entidad",
    element: (
      <RoleGuard permissions={[Permissions.expedient_base_entities.create]}>
        <EntityPage />
      </RoleGuard>
    ),
    handle: {
      breadcrumb: (match: { params?: { id?: string } }) => {
        const entity = `?entity=${match.params?.id}`;
        return [
          {
            label: "Expedientes",
            path: `/expedientes${match.params?.id ? entity : ""}`,
          },
          { label: "Nueva Entidad" },
        ];
      },
    },
  },
  {
    path: "/expedientes/entidad/:id?",
    element: (
      <RoleGuard permissions={[Permissions.expedient_base_entities.update]}>
        <EntityPage />
      </RoleGuard>
    ),
    loader: ({ params }: { params: Record<string, string | undefined> }) =>
      withNotFoundRedirect(
        () => createEntityDetailsBreadcrumb(queryClient, params.id || "")(),
        "/expedientes",
      ),
    handle: {
      breadcrumb: (match: {
        params?: { id?: string };
        loaderData?: { data?: { name?: string } };
      }) => {
        const entity = `?entity=${match.params?.id}`;
        return [
          {
            label: "Expedientes",
            path: `/expedientes${match.params?.id ? entity : ""}`,
          },
          { label: match?.loaderData?.data?.name ?? "Entidad" },
        ];
      },
    },
  },
  {
    path: "/expedientes/entidad/:id/registro",
    element: (
      <RoleGuard permissions={[Permissions.expedient_base_registries.create]}>
        <EntityRegistryPage />
      </RoleGuard>
    ),
    handle: {
      breadcrumb: (match: { params?: { id?: string } }) => {
        const entity = `?entity=${match.params?.id}`;
        return [
          {
            label: "Expedientes",
            path: `/expedientes${match.params?.id ? entity : ""}`,
          },
          { label: "Nuevo Registro" },
        ];
      },
    },
  },
  {
    path: "/expedientes/entidad/:id/registro/:registryId?",
    element: (
      <RoleGuard permissions={[Permissions.expedient_base_registries.update]}>
        <EntityRegistryPage />
      </RoleGuard>
    ),
    loader: ({ params }: { params: Record<string, string | undefined> }) =>
      withNotFoundRedirect(
        () =>
          createRegistryDetailsBreadcrumb(
            queryClient,
            params.registryId || "",
          )(),
        "/expedientes",
      ),
    handle: {
      breadcrumb: (match: {
        params?: { id?: string; registryId?: string };
        loaderData?: { data?: { name?: string } };
      }) => {
        const [entityName, registryName] = match?.loaderData?.data?.name?.split(
          "-separator-breadcrumb-",
        ) || ["", ""];
        const entity = `?entity=${match.params?.id}`;
        return [
          {
            label: "Expedientes",
            path: `/expedientes${match.params?.id ? entity : ""}`,
          },
          ...(entityName
            ? [
                {
                  label: entityName,
                  path: `/expedientes/entidad/${match.params?.id}/detalle-registro/${match.params?.registryId}`,
                },
              ]
            : []),
          { label: registryName || "Registro" },
        ];
      },
    },
  },
  {
    path: "/expedientes/entidad/:id/detalle-registro/:registryId",
    element: (
      <RoleGuard permissions={[Permissions.expedient_base_registries.read]}>
        <RegistryDetailPage />
      </RoleGuard>
    ),
    loader: ({ params }: { params: Record<string, string | undefined> }) =>
      withNotFoundRedirect(
        () =>
          createRegistryDetailsBreadcrumb(
            queryClient,
            params.registryId || "",
          )(),
        "/expedientes",
      ),
    handle: {
      breadcrumb: (match: {
        params?: { id?: string; registryId?: string };
        loaderData?: { data?: { name?: string } };
      }) => {
        const [entityName, registryName] = match?.loaderData?.data?.name?.split(
          "-separator-breadcrumb-",
        ) || ["", ""];
        const entity = `?entity=${match.params?.id}`;
        return [
          {
            label: "Expedientes",
            path: `/expedientes${match.params?.id ? entity : ""}`,
          },
          ...(entityName
            ? [
                {
                  label: entityName,
                  path: `/expedientes${match.params?.id ? entity : ""}`,
                },
              ]
            : []),
          { label: registryName || "Detalle del Registro" },
        ];
      },
    },
  },
  {
    path: "/expedientes/entidad/:id/detalle-registro/:registryId/tramite/:procedureId",
    element: (
      <RoleGuard
        permissions={[Permissions.expedient_base_registries["read-formality"]]}
      >
        <ProcedureDetailPage />
      </RoleGuard>
    ),
    handle: {
      breadcrumb: (match: {
        params?: RegistryParams;
        loaderData?: { data?: { name?: string } };
      }) => {
        const [entityName, registryName] = match?.loaderData?.data?.name?.split(
          "-separator-breadcrumb-",
        ) || ["", ""];
        const entity = `?entity=${match?.params?.id}`;
        return [
          {
            label: "Expedientes",
            path: `/expedientes${match?.params?.id ? entity : ""}`,
          },
          ...(entityName
            ? [
                {
                  label: entityName,
                  path: `/expedientes?entity=${match?.params?.id}`,
                },
              ]
            : []),
          { label: registryName || "Detalle del Registro" },
          { label: "Detalle de solicitud" },
        ];
      },
    },
    children: [
      {
        index: true,
        element: <ProcedureFormsList />,
        loader: ({ params }: { params: Record<string, string | undefined> }) =>
          withNotFoundRedirect(
            () =>
              createFormalityRequestBreadcrumb(
                queryClient,
                params.procedureId || "",
              )(),
            registryBase(params),
          ),
        handle: {
          breadcrumb: (match: {
            params?: RegistryParams;
            loaderData?: { data?: { name?: string } };
          }) => [
            {
              label: "Registro",
              path: registryBase(match?.params ?? {}),
            },
            { label: match?.loaderData?.data?.name || "Detalle" },
          ],
        },
      },
      {
        path: "correccion/:formId",
        element: (
          <RoleGuard
            permissions={[
              Permissions.widgets.submissions,
              Permissions.expedient_base_registries["read-formality"],
            ]}
            canHaveSomePermissions
          >
            <FormCorrection />
          </RoleGuard>
        ),
        loader: ({ params }: { params: Record<string, string | undefined> }) =>
          withNotFoundRedirect(
            () =>
              createGetFormDetailBreadcrumb(
                queryClient,
                params.formId || "",
                params.procedureId || "",
              )(),
            registryProcedureBase(params),
          ),
        handle: {
          breadcrumb: (match: {
            params?: RegistryParams;
            loaderData?: { data?: { name?: string } };
          }) => [
            { label: "Registro", path: registryBase(match?.params ?? {}) },
            {
              label: "Detalle",
              path: registryProcedureBase(match?.params ?? {}),
            },
            { label: match?.loaderData?.data?.name || "Nombre del Formulario" },
          ],
        },
      },
      {
        path: "firma/:singRequestId",
        element: (
          <RoleGuard
            permissions={[
              Permissions.widgets.submissions,
              Permissions.expedient_base_registries["read-formality"],
            ]}
            canHaveSomePermissions
          >
            <SignatureDetail />
          </RoleGuard>
        ),
        handle: {
          breadcrumb: (match: { params?: RegistryParams }) => [
            { label: "Registro", path: registryBase(match?.params ?? {}) },
            {
              label: "Detalle",
              path: registryProcedureBase(match?.params ?? {}),
            },
            { label: "Firma de documentos" },
          ],
        },
      },
      {
        path: "pago/:paymentRequestId",
        element: (
          <RoleGuard
            permissions={[
              Permissions.widgets.submissions,
              Permissions.expedient_base_registries["read-formality"],
            ]}
            canHaveSomePermissions
          >
            <PaymentDetail />
          </RoleGuard>
        ),
        handle: {
          breadcrumb: (match: { params?: RegistryParams }) => [
            { label: "Registro", path: registryBase(match?.params ?? {}) },
            {
              label: "Detalle",
              path: registryProcedureBase(match?.params ?? {}),
            },
            { label: "Detalle de pago" },
          ],
        },
      },
      {
        path: "resolucion",
        element: <ResolutionPage />,
        handle: {
          breadcrumb: (match: { params?: RegistryParams }) => [
            { label: "Registro", path: registryBase(match?.params ?? {}) },
            {
              label: "Detalle",
              path: registryProcedureBase(match?.params ?? {}),
            },
            { label: "Resolución" },
          ],
        },
      },
      {
        path: ":formId",
        element: <FormDetail />,
        loader: ({ params }: { params: Record<string, string | undefined> }) =>
          withNotFoundRedirect(
            () =>
              createGetFormDetailBreadcrumb(
                queryClient,
                params.formId || "",
                params.procedureId || "",
              )(),
            registryProcedureBase(params),
          ),
        handle: {
          breadcrumb: (match: {
            params?: RegistryParams;
            loaderData?: { data?: { name?: string } };
          }) => [
            { label: "Registro", path: registryBase(match?.params ?? {}) },
            {
              label: "Detalle",
              path: registryProcedureBase(match?.params ?? {}),
            },
            { label: match?.loaderData?.data?.name || "Nombre del Formulario" },
          ],
        },
      },
      {
        path: "formulario-interno/:formId",
        element: <FormInterns />,
        loader: ({ params }: { params: Record<string, string | undefined> }) =>
          withNotFoundRedirect(
            () =>
              createGetFormDetailBreadcrumb(
                queryClient,
                params.formId || "",
                params.procedureId || "",
              )(),
            registryProcedureBase(params),
          ),
        handle: {
          breadcrumb: (match: {
            params?: RegistryParams;
            loaderData?: { data?: { name?: string } };
          }) => [
            { label: "Registro", path: registryBase(match?.params ?? {}) },
            {
              label: "Detalle",
              path: registryProcedureBase(match?.params ?? {}),
            },
            { label: match?.loaderData?.data?.name || "Nombre del Formulario" },
          ],
        },
      },
    ],
  },
];
